import { decrementRequests, incrementRequests } from 'api/fetcher';
import axios, { AxiosRequestConfig } from 'axios';

import { Cookies } from '@hh.ru/browser-api-utils';

type UploadFilesProps = {
    videoData: FormData;
    thumbnailData: FormData;
    videoUrl: string;
    thumbnailUrl: string;
    signal?: AbortSignal;
    onProgress?: (percentage: number) => void;
};

type UploadIds = {
    videoUploadId: string;
    thumbnailUploadId: string;
};

const enum UploadResponseFields {
    UploadId = 'upload_id',
    DownloadUrl = 'download_url',
}

type UploadResponse = {
    [UploadResponseFields.UploadId]: string;
    [UploadResponseFields.DownloadUrl]: string | null;
};

export const uploadFiles = async ({
    videoData,
    thumbnailData,
    videoUrl,
    thumbnailUrl,
    signal,
    onProgress,
}: UploadFilesProps): Promise<UploadIds> => {
    const instance = axios.create();
    instance.defaults.timeout = 0;
    delete instance.defaults.headers.common['X-Requested-With'];
    delete instance.defaults.headers.common['X-Hh-Account-ID'];

    const xsrfCookie = Cookies.get('_xsrf') || '';

    const baseRequestConfig: AxiosRequestConfig = {
        headers: {
            'X-Xsrftoken': xsrfCookie,
        },
        signal,
    };

    const videoRequestConfig: AxiosRequestConfig = {
        ...baseRequestConfig,
        onUploadProgress: onProgress
            ? (progressEvent: ProgressEvent) => {
                  if (progressEvent.total) {
                      const percentage = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                      onProgress(percentage);
                  }
              }
            : undefined,
    } as AxiosRequestConfig;

    incrementRequests();
    try {
        const uploadVideoRequest = instance.post<UploadResponse>(videoUrl, videoData, videoRequestConfig);
        const uploadThumbnailRequest = instance.post<UploadResponse>(thumbnailUrl, thumbnailData, baseRequestConfig);

        const [uploadVideoResponse, uploadThumbnailResponse] = await Promise.all([
            uploadVideoRequest,
            uploadThumbnailRequest,
        ]);

        return {
            videoUploadId: uploadVideoResponse.data.upload_id,
            thumbnailUploadId: uploadThumbnailResponse.data.upload_id,
        };
    } catch (error) {
        throw error;
    } finally {
        decrementRequests();
    }
};
